import { noop } from 'lodash';
import { useEffect, useState } from 'react';
import { useLatest, useMountedState } from 'react-use';

import { delay } from '@/lib/util/delay';
import { parseCssDuration } from '@/lib/util/parseCssDuration';

import { useFunction } from './useFunction';

const DEFAULT_TRANSITION_DURATION = parseCssDuration('200ms');

export enum Stage {
    OPENING = 'opening',
    OPEN = 'open',
    CLOSING = 'closing',
    CLOSED = 'closed',
}

interface Params {
    appear?: boolean;
    onOpened?: () => void;
    onClosed?: () => void;
    transitionDuration?: number;
}

export interface TransitionState {
    stage: Stage;
    isMounted: boolean;
}

export function useTransition(state: boolean, params: Params = {}): TransitionState {
    const {
        appear = true,
        onOpened,
        onClosed,
        transitionDuration = DEFAULT_TRANSITION_DURATION,
    } = params;
    const initialStage = toStage(state, appear);
    const [stage, setStage] = useState<Stage>(initialStage);

    const callbacks = useLatest({ onOpened, onClosed });
    const isMounted = useMountedState();

    const open = useFunction(() => {
        if (!isMounted()) return noop;

        if (stage === Stage.OPEN || stage === Stage.OPENING) return noop;

        let cancel = false;

        setStage(Stage.OPENING);

        delay(transitionDuration * 1.5).then(() => {
            if (cancel || !isMounted()) return;
            setStage(Stage.OPEN);
            callbacks.current.onOpened?.();
        });

        return () => {
            cancel = true;
        };
    });

    const close = useFunction(() => {
        if (!isMounted()) return noop;

        if (stage === Stage.CLOSED || stage === Stage.CLOSING) return noop;

        let cancel = false;

        setStage(Stage.CLOSING);

        delay(transitionDuration * 1.5).then(() => {
            if (cancel || !isMounted()) return;
            setStage(Stage.CLOSED);
            callbacks.current.onClosed?.();
        });

        return () => {
            cancel = true;
        };
    });

    useEffect(() => (state ? open() : close()), [state, open, close]);

    return {
        stage,
        isMounted: stage !== Stage.CLOSED,
    };
}

function toStage(stage: Stage | boolean, appear: boolean): Stage {
    if (typeof stage === 'string') {
        return stage;
    }
    if (appear) {
        return Stage.CLOSED;
    }
    return stage ? Stage.OPEN : Stage.CLOSED;
}
